import {
    SortOptionsEnum,
    TransactionWithMatchMetadata,
    BillWithMatchMetadata,
    isPartialMatch
} from "./types";
import {CashFlowDirection} from "./BusinessCardFilter";
import {getSignedAmountFromDirection} from "./utils/utils.ts";

export const DEFAULT_SORT_OPTION: SortOptionsEnum = SortOptionsEnum.AMOUNT_DESC;

export function filterTransactionsByDirection(
    transactions: TransactionWithMatchMetadata[] ,
    direction: CashFlowDirection
): TransactionWithMatchMetadata[] {
    if (direction === 'all') {
        return transactions;
    }

    return transactions.filter(transaction => {
        const amount = transaction.total_amount_payable.signed_amount;
        if (direction === 'inflow') {
            return amount > 0;
        } else {
            return amount < 0;
        }
    });
}

export function filterDocumentsByDirection(
    documents: BillWithMatchMetadata[],
    direction: CashFlowDirection
): BillWithMatchMetadata[] {
    if (direction === 'all') {
        return documents;
    }

    return documents.filter(document => {
        const dir = isPartialMatch(document) ? document.bill_direction : document.document_direction;
        if (dir === 'sales_invoice') {
            return direction === 'inflow';
        }
        if (dir === 'purchase_invoice') {
            return direction === 'outflow';
        }
        return true;
    });
}

function getAmountValue(
    item: TransactionWithMatchMetadata | BillWithMatchMetadata
): number {
    if ('total_amount_payable' in item && item.total_amount_payable) {
        return item.total_amount_payable.signed_amount;
    }

    const scannedFields = (item as any).scanned_fields;
    if (scannedFields?.total_amount_payable) {
        return getSignedAmountFromDirection(scannedFields.total_amount_payable, (item as any).document_direction);
    }

    return 0;
}

function getDateValue(
    item: TransactionWithMatchMetadata | BillWithMatchMetadata
): number {
    if ('booking_date' in item && (item as any).booking_date) {
        return new Date((item as any).booking_date).getTime();
    }

    const scannedFields = (item as any).scanned_fields;
    if (scannedFields) {
        const dateString = scannedFields.normalized_datetime || scannedFields.date;
        return dateString ? new Date(dateString).getTime() : 0;
    }

    return 0;
}

export function sortItemsByOption<T extends TransactionWithMatchMetadata | BillWithMatchMetadata>(
    items: T[],
    sortOption: SortOptionsEnum
): T[] {
    const effectiveSort = sortOption === SortOptionsEnum.NONE ? DEFAULT_SORT_OPTION : sortOption;

    return [...items].sort((a, b) => {
        switch (effectiveSort) {
            case SortOptionsEnum.DATE_ASC:
                return getDateValue(a) - getDateValue(b);
            case SortOptionsEnum.DATE_DESC:
                return getDateValue(b) - getDateValue(a);
            case SortOptionsEnum.AMOUNT_ASC:
                return getAmountValue(a) - getAmountValue(b);
            case SortOptionsEnum.AMOUNT_DESC:
            default:
                return getAmountValue(b) - getAmountValue(a);
        }
    });
}
